import { FC, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { ControllerRenderProps } from 'react-hook-form';
import { getClientRunData, getRunDataByFund, projectAllFundsView } from '../../utils/runUtils';
import { IScenarioRunDataModel } from '../../../../../data-models/scenario-run.data-model';
import { ChipSelector } from '../../../../../components/ChipSelector';
import { RendererType } from '../../../../../data-models/field-definition';
import { FieldFactory } from '../../../../../components/Form/FieldFactory';
import { IScenarioRunGridProps, ScenarioRunGrid } from './ScenarioRunGrid';
import { ScenarioTypeByTransactions } from './ScenarioVisualization';

const ALL_FUNDS = 'All Funds';

interface IScenarioRunByFundProps extends IScenarioRunGridProps {
  initialClientFilterState: boolean;
  scenarioType: ScenarioTypeByTransactions;
}

export const ScenarioRunByFund: FC<IScenarioRunByFundProps> = ({
  colDefs,
  scenarioRunData,
  initialClientFilterState,
  scenarioType,
}) => {
  const dataByFund = useMemo(() => {
    return getRunDataByFund(scenarioRunData);
  }, [scenarioRunData]);

  const fundOptions = useMemo(
    () => [ALL_FUNDS, ...Array.from(dataByFund.keys()).sort((a, b) => a.localeCompare(b))],
    [dataByFund]
  );
  const [selectedFund, setSelectedFund] = useState(ALL_FUNDS);
  const [clientOnly, setClientOnly] = useState(initialClientFilterState);
  const isAllFunds = selectedFund === ALL_FUNDS;

  const gridData = useMemo(() => {
    if (isAllFunds) {
      // Skip the clientOnly filter — cap-table waterfall shows every share class.
      return projectAllFundsView(scenarioRunData);
    }
    const data = dataByFund.get(selectedFund);
    if (!clientOnly) {
      return data;
    }
    return getClientRunData(data ?? [], scenarioType);
  }, [clientOnly, dataByFund, isAllFunds, scenarioRunData, scenarioType, selectedFund]);

  return (
    <>
      <Stack direction={'row'} mr='3rem' alignItems={'center'}>
        <ScenarioRunFundSelector
          value={selectedFund}
          handleChange={(newValue: string) => {
            setSelectedFund(newValue);
          }}
          options={fundOptions}
        />
        <FieldFactory
          formProps={
            { value: isAllFunds ? false : clientOnly, onChange: setClientOnly } as ControllerRenderProps
          }
          formField={{ key: 'clientOnly', renderer: RendererType.boolean, disabled: isAllFunds }}
        />
        <Typography variant='body2' style={{ minWidth: '10rem', opacity: isAllFunds ? 0.5 : 1 }}>
          My Participation Only
        </Typography>
      </Stack>
      <ScenarioRunGrid
        key={selectedFund}
        colDefs={colDefs}
        scenarioRunData={(gridData as IScenarioRunDataModel[]) ?? []}
        scenarioType={scenarioType}
      />
    </>
  );
};

export const ScenarioRunFundSelector: FC<{
  options: string[];
  value: string;
  handleChange: (val: string) => void;
}> = ({ value, handleChange, options }) => {
  const fundOptions = useMemo(() => options.map((opt) => ({ value: opt, displayName: opt })), [options]);
  return <ChipSelector options={fundOptions} value={value} onChange={handleChange} />;
};
